import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { useUnpublishClip } from '../../hooks/use-clips';
import { notifyError, notifySuccess } from '../../lib/toast';

interface UnpublishClipDialogProps {
  open: boolean;
  onClose: () => void;
  clipId: string;
  clipTitle: string;
  // Called after a successful unpublish — e.g. to immediately open the delete dialog
  // when the admin's intent was "unpublish so I can delete".
  onUnpublished?: () => void;
}

// Confirm dialog for the unpublish action. Lighter tone than delete: the clip stays
// editable + re-publishable, just hidden from referees. We still gate it behind a confirm
// because it instantly changes what referees see — a misclick on the kebab shouldn't yank
// a live clip without a beat.
export function UnpublishClipDialog({ open, onClose, clipId, clipTitle, onUnpublished }: UnpublishClipDialogProps) {
  const { t } = useTranslation();
  const unpub = useUnpublishClip(clipId);

  async function onConfirm() {
    try {
      await unpub.mutateAsync();
      notifySuccess(t('admin.clips.unpublishDialog.confirmToast', { title: clipTitle }));
      onClose();
      onUnpublished?.();
    } catch (err) {
      notifyError(err, t('admin.clips.unpublishDialog.errorToast'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={unpub.isPending ? () => {} : onClose}
      title={t('admin.clips.unpublishDialog.title')}
      sub={clipTitle}
      panelClass="max-w-md"
      footer={
        <>
          <Button onClick={onClose} disabled={unpub.isPending}>
            {t('common.cancel')}
          </Button>
          {/* Primary (ink) variant — unpublish is a serious change but not destructive,
              so the same visual weight as Publish (just inverse meaning). */}
          <Button variant="primary" onClick={onConfirm} disabled={unpub.isPending}>
            {unpub.isPending ? <Spinner className="w-3.5 h-3.5" /> : <Icon.eyeOff className="w-3.5 h-3.5" />}
            {t('admin.clips.unpublishDialog.unpublishButton')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-ink-2">
        <p>{t('admin.clips.unpublishDialog.body1')}</p>
        <p className="text-muted">{t('admin.clips.unpublishDialog.body2')}</p>
      </div>
    </Modal>
  );
}
